/*
 * Model for landlords
 */

define([
  "backbone",
  "jquery",
  "messaging/messenger",
  "configuration/properties",
  "modules/header/kbMenu/kbMenuModel",
  "modules/shared/sortableList/sortableListModel",
], function (Backbone, $, Messenger, Properties, KbMenuModel, SortableListModel) {
  var LandlordModel = {};

  LandlordModel.landlord = Backbone.Model.extend(
    {
      idAttribute: "landlordId",
      defaults: {
        landlordName: "",
        tenantCapacity: {},
        kbCapacity: {},
        responseTypeCapacity: {},
        conversations: { enabled: true, inUse: false },
        responseVersioning: { enabled: true, inUse: false },
        taskManagement: { enabled: false, inUse: false },
        actionGroups: { enabled: false, inUse: false },
        agentAssist: { enabled: false, inUse: false },
        adHocReporting: { enabled: false, inUse: false },
        recommendations: { enabled: false, inUse: false },
        crossTouchPointAccess: { enabled: false, inUse: false },
        siteMapAccess: { enabled: false, inUse: false },
      },
      defaultValidateAttributes: [
        "landlordName",
        "tenantCapacity",
        "kbCapacity",
        "responseTypeCapacity",
      ],
      backup: function () {
        this.onSynced(function () {
          this.wasDirty = this.dirty;
          this.cloneCopy = this.clone();
        }, this);
      },
      restore: function () {
        this.set(this.cloneCopy.attributes);
        this.dirty = this.wasDirty;
      },
      _validateCapacity: function (value) {
        if (!value) return $.t("commonMessages.emptyMessage");
        let capacity = value.capacity;
        if (typeof capacity == "string") {
          if (!capacity) {
            return $.t("commonMessages.emptyMessage");
          }
          capacity = parseFloat(capacity);
        } else if (typeof capacity != "number") {
          return $.t("commonMessages.notNumeric");
        }
        if (isNaN(capacity) || !isFinite(capacity)) return $.t("commonMessages.notNumeric");
        if (Math.round(capacity) != capacity) return $.t("commonMessages.notInteger");
        if (capacity <= 0) return $.t("commonMessages.notPositive");
        if (capacity > 2147483647) return $.t("commonMessages.tooBig", { number: 2147483647 });
        if (capacity < value.usage)
          return $.t("admin.landlordAdmin.usageExceedsCapacity", { count: value.usage });
        value.capacity = capacity;
      },
      _validateDuplicateLandlordName: function (value) {
        if (this.collection) {
          var duplicateName = false;
          this.collection.each(function (landlord) {
            //check if the other object has the same name (ignore case) and is actually a different object
            if (
              landlord.get("landlordName").toLowerCase() === value.toLowerCase() &&
              landlord.cid !== this.cid
            ) {
              duplicateName = true;
            }
          }, this);
          if (duplicateName) return $.t("admin.landlordAdmin.duplicateName");
        }
      },
      _validateMoreKbsThanTenants: function (kbCapacity) {
        const tenantCapacity = this.get("tenantCapacity");
        const tenantCapacityError = this._validateCapacity(tenantCapacity);
        if (
          !tenantCapacityError &&
          parseInt(tenantCapacity.capacity, 10) > parseInt(kbCapacity.capacity, 10)
        ) {
          return $.t("admin.landlordAdmin.fewerKBsThanTenants");
        }
      },
      validateAttribute: function (attribute, value) {
        if (attribute === "landlordName") {
          return (
            this.validateRequiredValue(value, { maxLength: 50 }) ||
            this.validateSafeCharacters(value) ||
            this._validateDuplicateLandlordName(value)
          );
        }
        if (attribute === "tenantCapacity") {
          return this._validateCapacity(value);
        }
        if (attribute === "kbCapacity") {
          return this._validateCapacity(value) || this._validateMoreKbsThanTenants(value);
        }
        if (attribute === "responseTypeCapacity") {
          return this._validateCapacity(value);
        }
        if ($.inArray(attribute, LandlordModel.landlord.KBSettings) > -1) {
          /* if inuse is true then enabled must be true */
          if (LandlordModel.landlord.isKBSettingValid(value))
            return $.t("admin.landlordAdmin.attributeIsInUse");
        }
      },
    },
    {
      /* class (static) members */
      // list of all KB settings - these settings are processed in a similar way.
      KBSettings: [
        "taskManagement",
        "actionGroups",
        "agentAssist",
        "adHocReporting",
        "recommendations",
        "crossTouchPointAccess",
        "siteMapAccess",
      ],
      isKBSettingValid: function (setting) {
        return setting.inUse && !setting.enabled;
      },
      // should a setting checkbox tooltip be added?
      settingTooltipEnabled: function (setting, editMode) {
        // if !editMode and inuse and enabled
        return !editMode && setting.enabled && setting.inUse;
      },
      // should a settingCheckBox be enabled?
      settingCheckBoxEnabled: function (setting, editMode) {
        // if editMode || (inuse and enabled)
        return !(editMode || (setting.enabled && setting.inUse));
      },
    },
  );

  LandlordModel.landlords = Backbone.Collection.extend({
    model: LandlordModel.landlord,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "landlordName");
    },
    addNewLandlord: function () {
      var newLandlord = new LandlordModel.landlord();
      this.add(newLandlord);
      return newLandlord;
    },
    url: function () {
      return Properties.adminRootURL + "/landlords";
    },
  });

  Messenger.reqres.setHandler("fetch:landlord", function (landlordId) {
    return new LandlordModel.landlord({ landlordId: landlordId });
  });

  Messenger.reqres.setHandler(
    "landlords",
    function () {
      var landlords = new LandlordModel.landlords();
      landlords.fetch();
      //    landlords.fetch({ validate: true });
      return landlords;
    },
    this,
  );

  Messenger.reqres.setHandler("landlord:list:headers", function () {
    var Header = SortableListModel.headerModel;
    return new Backbone.Collection([
      new Header({
        headerName: $.t("admin.landlordAdmin.landlordName"),
        modelAttribute: "landlordName",
        selectable: true,
        sortable: true,
        textField: true,
      }),
      new Header({
        headerName: $.t("admin.landlordAdmin.tenantCapacity"),
        modelAttribute: "tenantCapacity",
        customHtml: "<%- tenantCapacity.usage %> / <%- tenantCapacity.capacity %>",
      }),
      new Header({
        headerName: $.t("admin.landlordAdmin.knowledgebaseCapacity"),
        modelAttribute: "kbCapacity",
        customHtml: "<%- kbCapacity.usage %> / <%- kbCapacity.capacity %>",
      }),
      new Header({
        headerName: $.t("admin.landlordAdmin.responseTypeCapacity"),
        modelAttribute: "responseTypeCapacity",
        customHtml: "<%- responseTypeCapacity.usage %> / <%- responseTypeCapacity.capacity %>",
      }),
      new Header({
        headerName: "",
        modelAttribute: "landlordName",
        width: "15px",
        customHtml:
          '<button type="button" class="btn btn-danger btn-mini deleteLandlord\
        <% if (tenantCapacity.usage > 0) { %> disabled" data-i18n="[title]admin.landlordAdmin.deleteLandlordInUse" <% } else { %>"\
        data-i18n="[title]admin.landlordAdmin.deleteLandlord" <% } %> data-toggle="tooltip">\
          <i class="icon-remove"></i></button> ',
      }),
    ]);
  });

  Messenger.reqres.setHandler("fetch:landlord:id", function (landlordId) {
    var landlord = new LandlordModel.landlord();
    landlord.fetch({ url: Properties.adminRootURL + "/landlords/" + landlordId });
    return landlord;
  });

  return LandlordModel;
});
